import { formatDate } from 'secullum-react-native-ui';
import AsyncStorage from '@react-native-async-storage/async-storage';
import AvisoDivergenciaFusoHorario from '../containers/AvisoDivergenciaFusoHorarioContainer';
import Funcionario from '../containers/FuncionarioContainer';

const CHAVE_MODIFICACAO_FUSO = 'ModificacaoFusoHorario';
// O aviso de fuso é mostrado uma vez por contexto (online/offline), e não uma única vez
// no total, porque o comportamento e o texto exibido mudam de acordo com o contexto.
const CHAVE_AVISO_DIVERGENCIA_FUSO_ONLINE = 'AvisoDivergenciaFusoOnlineExibido';
const CHAVE_AVISO_DIVERGENCIA_FUSO_OFFLINE =
  'AvisoDivergenciaFusoOfflineExibido';

interface DadosModificacaoFuso {
  fusoFuncionario: string;
  usuarioModificouFusoDispositivo: boolean;
}

export async function salvarFusoFuncionarioAsync(fusoFuncionario: string) {
  const dados: DadosModificacaoFuso = {
    fusoFuncionario,
    usuarioModificouFusoDispositivo: false
  };

  await AsyncStorage.setItem(CHAVE_MODIFICACAO_FUSO, JSON.stringify(dados));
}

export async function verificarFusoModificadoAsync(
  fusoMinutosFuncionario: number
): Promise<boolean> {
  const jsonDados = await AsyncStorage.getItem(CHAVE_MODIFICACAO_FUSO);

  if (jsonDados == null) {
    return false;
  }

  const dados: DadosModificacaoFuso = JSON.parse(jsonDados);

  if (dados.usuarioModificouFusoDispositivo) {
    return true;
  }

  const fusoDispositivo = (-new Date().getTimezoneOffset()).toString();

  if (fusoMinutosFuncionario.toString() !== fusoDispositivo) {
    dados.usuarioModificouFusoDispositivo = true;

    await AsyncStorage.setItem(CHAVE_MODIFICACAO_FUSO, JSON.stringify(dados));

    return true;
  }

  return false;
}

function formatarFuso(minutos: number): string {
  const sinal = minutos >= 0 ? '+' : '-';
  const abs = Math.abs(minutos);
  const horas = Math.floor(abs / 60);
  const mins = abs % 60;

  if (mins === 0) {
    return `UTC${sinal}${horas}`;
  }

  return `UTC${sinal}${horas}:${mins.toString().padStart(2, '0')}`;
}

function formatarFusoComHorarioConhecido(
  minutos: number,
  horario: Date
): string {
  return `${formatarFuso(minutos)} (${formatDate(horario, 'HH:mm')})`;
}

// Sem um horário de servidor disponível (ex: offline), não há como saber a hora exata da empresa
// — calcula a partir do relógio do próprio aparelho, assumindo que ele esteja correto.
function formatarFusoComHorarioCalculado(minutos: number): string {
  const now = new Date();
  const timeAtOffset = new Date(now.getTime() + minutos * 60000);
  const hh = timeAtOffset.getUTCHours().toString().padStart(2, '0');
  const mm = timeAtOffset.getUTCMinutes().toString().padStart(2, '0');

  return `${formatarFuso(minutos)} (${hh}:${mm})`;
}

async function avisoDivergenciaFusoExibidoAsync(
  contexto: 'online' | 'offline'
): Promise<boolean> {
  const chave =
    contexto === 'online'
      ? CHAVE_AVISO_DIVERGENCIA_FUSO_ONLINE
      : CHAVE_AVISO_DIVERGENCIA_FUSO_OFFLINE;
  const valor = await AsyncStorage.getItem(chave);

  return valor === 'true';
}

export async function marcarAvisoDivergenciaFusoExibidoAsync(
  contexto: 'online' | 'offline'
): Promise<void> {
  const chave =
    contexto === 'online'
      ? CHAVE_AVISO_DIVERGENCIA_FUSO_ONLINE
      : CHAVE_AVISO_DIVERGENCIA_FUSO_OFFLINE;

  await AsyncStorage.setItem(chave, 'true');
}

async function obterDivergenciaFusoAsync(
  fusoMinutosFuncionario: number,
  contexto: 'online' | 'offline',
  horaServidorParaExibicao?: Date
): Promise<{ valorDispositivo: string; valorEmpresa: string } | null> {
  const fusoJaExibido = await avisoDivergenciaFusoExibidoAsync(contexto);

  if (fusoJaExibido) {
    return null;
  }

  const fusoModificado = await verificarFusoModificadoAsync(
    fusoMinutosFuncionario
  );

  if (!fusoModificado) {
    return null;
  }

  const valorEmpresa = horaServidorParaExibicao
    ? formatarFusoComHorarioConhecido(
        fusoMinutosFuncionario,
        horaServidorParaExibicao
      )
    : formatarFusoComHorarioCalculado(fusoMinutosFuncionario);

  return {
    valorDispositivo: formatarFusoComHorarioConhecido(
      -new Date().getTimezoneOffset(),
      new Date()
    ),
    valorEmpresa
  };
}

export async function verificarEAtivarDivergenciaFusoAsync(
  fusoMinutosFuncionario: number,
  contexto: 'online' | 'offline',
  horaServidorParaExibicao?: Date
): Promise<boolean> {
  const divergencia = await obterDivergenciaFusoAsync(
    fusoMinutosFuncionario,
    contexto,
    horaServidorParaExibicao
  );

  if (!divergencia) {
    return false;
  }

  AvisoDivergenciaFusoHorario.ativar(
    'fuso',
    contexto,
    divergencia.valorDispositivo,
    divergencia.valorEmpresa
  );

  return true;
}

export async function verificarEAtivarDivergenciaFusoFuncionarioAsync(
  contexto: 'online' | 'offline',
  horaServidorParaExibicao?: Date
): Promise<boolean> {
  const fusoMinutosFuncionario = Funcionario.state.dados.fusoMinutos;

  return await verificarEAtivarDivergenciaFusoAsync(
    fusoMinutosFuncionario,
    contexto,
    horaServidorParaExibicao
  );
}
